import { decodePairingCode, encodePairingCode, isPairingExpired, toBase64, type PairingPayload } from "@foreman/protocol";
import { clearGoogleState, createHandoffCode, getSigned, pendingGoogleState, redeemHandoffCode, subscribeSigned } from "./account";
import { installHint, installedApp, iosBrowserTab } from "./install-hint";
import type { View } from "./nav";

/**
 * An iPhone's Home Screen app starts with storage of its own: what a Safari tab paired is not there. So on an iPhone browser tab the
 * pairing is finished in the Home Screen app instead. The one-time code (valid for minutes, single use, so nothing long-lived)
 * rides along in the page's address, which iOS keeps as the app's start address when the page is added, and in a short cookie
 * as a second way to reach the same place. The app reads it on its first launch and goes straight to the confirm step.
 */
const COOKIE = "bc_pair";
let skipped = false; // "Pair here instead": no more install-first this visit

/** The address fragment for a code, with the computer's name when it had one. */
export function pairHash(p: PairingPayload): string {
  return `pair=${encodePairingCode(p)}${p.name ? `&name=${encodeURIComponent(p.name)}` : ""}`;
}

/** A phone in a browser tab that can add the app to its Home Screen (iPhone): the code goes to the app, not paired here. */
export function installFirst(): boolean {
  return !skipped && installHint()?.kind === "ios";
}
export function pairHere(): void {
  skipped = true;
  unstashPairing();
}

/** Which screen follows a code: the install step on an iPhone browser tab, otherwise the confirm step. */
export function confirmViewFor(payload: PairingPayload): View {
  return installFirst() ? { name: "installfirst", payload } : { name: "confirm", payload };
}

let stashed: PairingPayload | null = null; // the pairing code on this page's address
let signinCode: string | null = null; // the sign-in hand-off code on it

/** What the address and the cookie carry now: the pairing code and/or the sign-in code, whichever there are. */
function render(): void {
  const parts = [stashed ? pairHash(stashed) : "", signinCode ? `signin=${signinCode}` : ""].filter(Boolean);
  const hash = parts.join("&");
  try {
    history.replaceState(null, "", `${location.pathname}${location.search}${hash ? `#${hash}` : ""}`);
  } catch {
    /* the cookie below may still do it */
  }
  try {
    const secs = hash ? (signinCode ? 15 * 60 : Math.max(1, Math.floor((stashed?.expiresAt ?? 0) - Date.now() / 1000))) : 0;
    document.cookie = `${COOKIE}=${hash ? encodeURIComponent(hash) : ""}; max-age=${secs}; path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  } catch {
    /* no cookies: the address alone */
  }
}

/** Put the code in the address and a cookie, so the Home Screen app made from this page finds it. */
export function stashPairing(p: PairingPayload): void {
  stashed = p;
  render();
}

export function unstashPairing(): void {
  stashed = null;
  render();
}

/**
 * Signed in, in an iPhone browser tab: keep a fresh one-time sign-in code on the address, so a Home Screen app made from this page
 * can sign itself in as the same account (its storage is separate, and Google can't be asked again without typing). Codes work once
 * and end after 15 minutes, so one is made again every 10 minutes and whenever the page comes back to the front.
 */
export function startSignInHandoff(): () => void {
  if (!iosBrowserTab()) return () => {};
  let live = true;
  let busy = false;
  const refresh = async () => {
    if (!live || busy || !getSigned()) return;
    busy = true;
    const code = await createHandoffCode();
    busy = false;
    if (live && getSigned() && code) {
      signinCode = code;
      render();
    }
  };
  const onSigned = () => {
    if (!getSigned() && signinCode) {
      signinCode = null;
      render();
    } else void refresh();
  };
  const onShow = () => document.visibilityState === "visible" && void refresh();
  const off = subscribeSigned(onSigned);
  const timer = setInterval(() => void refresh(), 10 * 60_000);
  document.addEventListener("visibilitychange", onShow);
  void refresh();
  return () => {
    live = false;
    off();
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onShow);
  };
}

/** The sign-in code left for the Home Screen app (by address or cookie), if any. */
export function handedOverSignInCode(): string | null {
  const find = (text: string) => /(?:^|[#&;\s])signin=([0-9a-f]{32})/.exec(text)?.[1] ?? null;
  try {
    const fromAddress = find(location.hash);
    if (fromAddress) return fromAddress;
    const m = new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]+)`).exec(document.cookie);
    return m ? find("&" + decodeURIComponent(m[1]!)) : null;
  } catch {
    return null;
  }
}

/** First launch of the installed app: sign in with the code the page left, then clear it from the address. */
export async function redeemHandedOverSignIn(): Promise<boolean> {
  if (!installedApp() || getSigned()) return false;
  const code = handedOverSignInCode();
  if (!code) return false;
  // A sign-in this app sent out to Google comes back with the secret it made; one without it is not ours to accept.
  const out = pendingGoogleState();
  const back = /[#&]state=([0-9a-f]{32})/.exec(location.hash)?.[1] ?? null;
  const ok = out && back !== out ? false : await redeemHandoffCode(code);
  if (out) clearGoogleState();
  try {
    if (/signin=/.test(location.hash)) history.replaceState(null, "", location.pathname + location.search + location.hash.replace(/(^#|&)(signin|state)=[0-9a-f]{32}/g, "").replace(/^#?&/, "#").replace(/^#$/, ""));
    document.cookie = `${COOKIE}=; max-age=0; path=/`;
  } catch {
    /* nothing to clear */
  }
  return ok;
}

/** The code a Home Screen app's first launch was handed through the cookie, if it is still good. */
export function stashedPairing(): PairingPayload | null {
  try {
    const m = new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]+)`).exec(document.cookie);
    if (!m) return null;
    const text = decodeURIComponent(m[1]!);
    const p = decodePairingCode(text);
    const name = /[&]name=([^&]+)/.exec(text);
    return isPairingExpired(p) ? null : { ...p, ...(name && !p.name ? { name: decodeURIComponent(name[1]!) } : {}) };
  } catch {
    return null;
  }
}

/** Already paired with this computer on this phone (a start address kept by the Home Screen outlives the one-time code). */
export function alreadyPaired(p: PairingPayload): boolean {
  try {
    const list = JSON.parse(localStorage.getItem("foreman.computers.v1") ?? "[]") as { daemonPublicKey?: string }[];
    return list.some((c) => c.daemonPublicKey === toBase64(p.daemonPublicKey));
  } catch {
    return false;
  }
}

/** First launch of the installed app: a code left for it (by address or cookie) takes it to the confirm step. */
export function handedOverPairing(fromHash: PairingPayload | null): PairingPayload | null {
  if (!installedApp()) return null;
  const p = fromHash ?? stashedPairing();
  return p && !isPairingExpired(p) && !alreadyPaired(p) ? p : null;
}
